import { SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { LatLngBoundsExpression } from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import { Identifier, useTranslate } from 'react-admin';
import { useTheme } from '@mui/material';
import {
  MapContainer, ScaleControl, useMap,
} from 'react-leaflet';
import { ItemMapMarker } from './ItemMapMarker';
import { CenterControl, CustomLayerControl, FloorFilter } from '../../../components/LeafletControls';
import { hasItemPosition, itemToLatLng } from '../util';
import { useOverlay } from '../../../../../utils/useOverlay';

const FitBoundsToItems = ({ items, trigger }: { items: SecurityInventoryItem[]; trigger: number }) => {

  const map = useMap();
  useEffect(() => {

    const coords = items.filter(hasItemPosition).map(itemToLatLng);
    if (coords.length === 0) return;
    map.fitBounds(coords as LatLngBoundsExpression, { maxZoom: 19 });

  }, [map, items, trigger]);
  return null;

};

const getItemFloor = (item: SecurityInventoryItem) => {

  return item.location.properties?.address?.floor || 0;

};

export const InventoryItemsMap = (props: {
  mapRef: React.Dispatch<React.SetStateAction<L.Map | null>>;
  items: SecurityInventoryItem[];
  categories: SecurityInventoryCategory[];
  focusedItemId: Identifier | null;
  hoveredItemId: Identifier | null;
  setFocusedItemId: React.Dispatch<React.SetStateAction<Identifier | null>>;
}) => {

  const [map, setMap] = useState<L.Map | null>(null);

  const [selectedFloors, setSelectedFloors] = useState<number[]>([]);
  const [fitTrigger, setFitTrigger] = useState(0);

  const [moveLocked, setMoveLocked] = useState(true);

  const translate = useTranslate();

  const theme = useTheme();
  const overlays = useOverlay(map, selectedFloors);

  const possibleFloors = useMemo(() => {

    const itemFloors = props.items.map(getItemFloor).filter((f): f is number => typeof f === 'number');
    const overlayFloors = overlays.map((o) => o.level).filter((l): l is number => typeof l === 'number');
    return Array.from(new Set([...itemFloors, ...overlayFloors])).sort((a, b) => a - b);

  }, [props.items, overlays]);

  const visibleItems = useMemo(() => {

    if (selectedFloors.length === 0) return props.items;
    return props.items.filter((item) => {

      const floor = getItemFloor(item);
      return floor !== null && selectedFloors.includes(floor);

    });

  }, [props.items, selectedFloors]);

  useEffect(() => {

    setFitTrigger((x) => x + 1);

  }, [selectedFloors]);

  return (
    <MapContainer
      ref={(ref: L.Map) => {

        setMap(ref);
        props.mapRef(ref);

      }}
      center={[52.1009166, 5.6462914]}
      zoom={17}
      id="inventory-items-map-container"
      minZoom={4}
      zoomControl={false}
      style={{ height: '100%', width: '100%' }}
    >
      <div>
        <div className="leaflet-control-container-custom">
          <CustomLayerControl map={map} toggleVisible={true}/>
          <CenterControl onClick={() => {

            setFitTrigger((x) => x + 1);

          }}/>
          <button
            onClick={() => setMoveLocked((v) => !v)}
            className="map-control-button"
            style={moveLocked ? {
              backgroundColor: theme.palette.error.main,
              color: 'white',
            } : {
              backgroundColor: theme.palette.primary.main,
              color: 'white',
            }}
          >
            {moveLocked ? <LockIcon /> : <LockOpenIcon />}
            <span>{translate(moveLocked ? 'resources.buttons.text.locked' : 'resources.buttons.text.unlocked')}</span>
          </button>
        </div>
        <div className="second-filter-div" style={{
          left: '260px',
        }}>
          <FloorFilter floors={possibleFloors} selectedFloors={selectedFloors} onChange={setSelectedFloors}/>
        </div>
      </div>

      <ScaleControl position="bottomright" imperial={false} />

      <FitBoundsToItems items={visibleItems} trigger={fitTrigger} />

      {visibleItems.filter(hasItemPosition).map((item) => (
        <ItemMapMarker
          key={item._id}
          item={item}
          categories={props.categories}
          focusedItemId={props.focusedItemId}
          setFocusedItemId={props.setFocusedItemId}
          hoveredItemId={props.hoveredItemId}
          moveLocked={moveLocked}
        />
      ))}
    </MapContainer>
  );

};
